import { css } from '@emotion/react';
import React, { forwardRef } from 'react';

import {
  borderRadius,
  colors,
  fonts,
  fontSizes,
  fontWeights,
  lineHeights,
  space,
} from '../shared/styles';
import { BaseInputProps } from './Input';

export interface SelectOption {
  /**
   * Option value
   */
  value: string | number;
  /**
   * Option label text
   */
  label: string;
  /**
   * Whether the option is disabled
   */
  disabled?: boolean;
}

export interface SelectProps
  extends BaseInputProps,
    Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  /**
   * Select options
   */
  options: SelectOption[];
  /**
   * Placeholder option text
   */
  placeholderOption?: string;
}

const selectStyles = css`
  position: relative;
  width: 100%;
  font-family: ${fonts.base};
  font-weight: ${fontWeights.normal};
  border: 1px solid ${colors.gray['300']};
  border-radius: ${borderRadius.base};
  background-color: ${colors.light['100']};
  color: ${colors.gray['900']};
  transition: all 0.2s ease-in-out;
  appearance: none;
  cursor: pointer;

  /* Custom dropdown arrow */
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right ${space['3']} center;
  background-size: 16px;
  padding-right: ${space['10']};

  &:focus {
    outline: none;
    border-color: ${colors.olive['500']};
    box-shadow: 0 0 0 3px ${colors.olive['100']};
  }

  &:disabled {
    background-color: ${colors.gray['100']};
    color: ${colors.gray['500']};
    cursor: not-allowed;
    opacity: 0.6;
  }

  &:read-only {
    background-color: ${colors.gray['100']};
    cursor: default;
  }
`;

const sizeStyles = {
  sm: css`
    padding-top: 0;
    padding-bottom: 0;
    padding-left: ${space['3']};
    font-size: ${fontSizes.sm};
    line-height: ${lineHeights.tight};
    height: ${space['8']};
  `,
  md: css`
    padding-top: 0;
    padding-bottom: 0;
    padding-left: ${space['4']};
    font-size: ${fontSizes.base};
    line-height: ${lineHeights.normal};
    height: ${space['10']};
  `,
  lg: css`
    padding-top: 0;
    padding-bottom: 0;
    padding-left: ${space['5']};
    font-size: ${fontSizes.lg};
    line-height: ${lineHeights.normal};
    height: ${space['12']};
  `,
};

const variantStyles = {
  default: css``,
  error: css`
    border-color: ${colors.red['500']};

    &:focus {
      border-color: ${colors.red['500']};
      box-shadow: 0 0 0 3px ${colors.red['100']};
    }
  `,
  success: css`
    border-color: ${colors.accent.green};

    &:focus {
      border-color: ${colors.accent.green};
      box-shadow: 0 0 0 3px rgba(76, 168, 23, 0.1);
    }
  `,
};

const optionStyles = css`
  background-color: ${colors.light['100']};
  color: ${colors.gray['900']};

  &:disabled {
    color: ${colors.gray['500']};
    background-color: ${colors.gray['100']};
  }
`;

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      size = 'md',
      variant = 'default',
      options,
      placeholderOption,
      disabled = false,
      className = '',
      ...props
    },
    ref
  ) => {
    return (
      <select
        ref={ref}
        disabled={disabled}
        css={[selectStyles, sizeStyles[size], variantStyles[variant]]}
        className={className}
        {...props}
      >
        {placeholderOption && (
          <option value="" disabled css={optionStyles}>
            {placeholderOption}
          </option>
        )}
        {options.map((option) => (
          <option
            key={option.value}
            value={option.value}
            disabled={option.disabled}
            css={optionStyles}
          >
            {option.label}
          </option>
        ))}
      </select>
    );
  }
);

Select.displayName = 'Select';
